import { z } from 'zod/v4';

import { analyticsSettingsSchemaLegacy } from '../../../common/schemas/legacy/analytics';
import { extendedPositionSchemaLegacy } from '../../../common/schemas/legacy/extended-position';
import { visibilityPluginShapeLegacy } from '../../../common/schemas/legacy/visibility';
import { widgetPositionSchemaLegacy } from '../../../common/schemas/legacy/widget-position';

import { chatBotDataSchema, chatBotsButtonSettingsSchema } from './chat-bot';
import { crmPipelineSchema, crmPipelineSettingSchema, crmPipelineStepSchema } from './crm';
import { formElementSchema } from './form-element';
import { formAdditionalSettingsSchema, formButtonSettingsSchema, formInputSettingsSchema } from './form-settings';

/**
 * Form rendering mode, mirroring `EFormView`.
 */
export const formViewSchema = z.enum(['static', 'modal']);

/**
 * Field shape shared by both placement variants of the subscription-form
 * widget — mirrors `FormWidgetData`
 * (`apps/websites/src/app/widgets/form/type/form-widget.type.ts`) and the
 * defaults produced by `formData()` in the host model. The variants diverge
 * **only on `position`**: default placement keeps the simple per-device grid,
 * zero placement uses the extended-position grid (unlike video, `hideOn*` and
 * `isSynchronizeStyle` are common — the form always carried them).
 *
 * Scalars the host factory seeds with `null` are `nullable()`; groups/flags
 * added over the widget's lifetime are `nullish()` (older persisted forms
 * omit them). The schema also normalises what old forms persisted: an absent
 * `createContactAndDeal` means `true`, an absent / `null` `formView` means
 * `'static'`.
 */
const formCommonDataShape = {
    // CRM integration
    selectedAddressBook: z.number().nullable(),
    selectedPipeline: crmPipelineSchema.nullable(),
    selectedPipelineStep: crmPipelineStepSchema.nullable(),
    selectedPipelineSettings: z.array(crmPipelineSettingSchema).nullable(),
    createContactAndDeal: z
        .boolean()
        .nullish()
        .transform((value) => (value === undefined ? true : value)),
    dealName: z.string().nullable(),

    // Address book / tags
    createAddressBook: z.boolean().nullable(),
    addCrmTags: z.boolean().nullable(),
    contactTags: z.array(z.number()),
    addAddressBookTags: z.boolean().nullish(),
    contactAddressBookTags: z.array(z.number()).nullish(),

    // Chat bots
    addChatBotToForm: z.boolean().nullable(),
    chatBots: z.array(chatBotDataSchema),
    chatBotsButtonSettings: chatBotsButtonSettingsSchema,

    // Form content & styling
    elements: z.array(formElementSchema),
    inputSettings: formInputSettingsSchema,
    formButtonSettings: formButtonSettingsSchema,
    modalFormButtonSettings: formButtonSettingsSchema,
    additionalFormSettings: formAdditionalSettingsSchema,
    analyticsSettings: analyticsSettingsSchemaLegacy.nullish(),

    // View mode
    formView: formViewSchema.nullish().transform((view) => view ?? 'static'),
    isTwoStepForm: z.boolean().nullable(),
    modalTitle: z.string().nullable(),
    isSynchronizeStyle: z.boolean().nullable(),

    // Visibility
    ...visibilityPluginShapeLegacy,
} as const;

/**
 * Data shape for a **default-placement** form widget — common fields plus the
 * simple per-device position grid.
 */
export const formLegacyDefaultDataSchema = z.object({
    ...formCommonDataShape,
    position: widgetPositionSchemaLegacy,
});

/**
 * Data shape for a **zero-block-placement** form widget — common fields plus
 * the extended-position grid (per-device width/height/x/y/rotate/zIndex).
 */
export const formLegacyZeroDataSchema = z.object({
    ...formCommonDataShape,
    position: extendedPositionSchemaLegacy,
});
